import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster, toast } from 'sonner';
import axios from 'axios';

// Configure axios to always send credentials (cookies) with requests
axios.defaults.withCredentials = true;

// Context
const AuthContext = createContext(null);
const CartContext = createContext(null);
const StoreSettingsContext = createContext(null);

const API = '/api';

// Default store settings (fallback)
const DEFAULT_STORE_SETTINGS = {
  free_shipping_threshold: 15,  // Free shipping for orders £15 or over
  minimum_order_value: 15,
  small_order_surcharge: 0.50,
  bnpl_surcharge_percent: 3.5,
  bnpl_surcharge_fixed: 0.50,
  web_pricing_percent: 3
};

// Store Settings Provider
export const StoreSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_STORE_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/store-settings`);
        if (response.data && !response.data.error) {
          setSettings(response.data);
        }
      } catch (error) {
        console.log('Using default store settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  return (
    <StoreSettingsContext.Provider value={{ settings, loading }}>
      {children}
    </StoreSettingsContext.Provider>
  );
};

export const useStoreSettings = () => useContext(StoreSettingsContext);

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(response.data);
    localStorage.setItem('token', response.data.token);
    // Auto-redirect staff to their dashboard
    const roles = response.data.roles || [];
    if (roles.includes('staff') && !window.location.pathname.startsWith('/staff-dashboard')) {
      setTimeout(() => { window.location.href = '/staff-dashboard'; }, 100);
    }
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    setUser(response.data);
    localStorage.setItem('token', response.data.token);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
    localStorage.removeItem('token');
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, googleLogin, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Cart Provider
export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0, shipping: 0, total: 0 });
  const [cartOpen, setCartOpen] = useState(false);
  const { user } = useAuth();
  const { settings: storeSettings } = useStoreSettings();

  const fetchCart = useCallback(async () => {
    if (user) {
      // Logged in - fetch from server
      try {
        const response = await axios.get(`${API}/cart`, { withCredentials: true });
        setCart(response.data);
      } catch {
        setCart({ items: [], subtotal: 0, shipping: 0, total: 0 });
      }
    } else {
      // Guest - load from localStorage
      const saved = localStorage.getItem('guest_cart');
      if (saved) {
        const guestItems = JSON.parse(saved);
        
        // Sanitize prices — guard against stale/corrupt localStorage data
        for (const item of guestItems) {
          const price = parseFloat(item.unit_price) || parseFloat(item.price) || parseFloat(item.product?.price) || 0;
          item.unit_price = price;
          item.price = price;
          item.quantity = parseInt(item.quantity, 10) || 1;
          item.item_total = price * item.quantity;
        }
        
        const subtotal = guestItems.reduce((sum, item) => sum + item.item_total, 0);
        const totalWeight = guestItems.reduce((sum, item) => {
          let w = parseFloat(item.weight) || 0.5;
          if (w > 50) w = w / 1000;
          return sum + (w * item.quantity);
        }, 0);
        
        // Use dynamic store settings for shipping calculation
        const freeShippingThreshold = storeSettings?.free_shipping_threshold || 350;
        const minOrderValue = storeSettings?.minimum_order_value || 15;
        const smallOrderFee = storeSettings?.small_order_surcharge || 0.50;
        
        let shipping = 0;
        if (subtotal < freeShippingThreshold) {
          if (totalWeight <= 1) shipping = 4.00;
          else if (totalWeight <= 2) shipping = 5.48;
          else if (totalWeight <= 5) shipping = 7.49;
          else if (totalWeight <= 10) shipping = 7.49;
          else if (totalWeight <= 15) shipping = 11.99;
          else shipping = 11.99 + (Math.floor((totalWeight - 15) / 15) + 1) * 11.99;
        }
        
        const smallOrderSurcharge = subtotal < minOrderValue ? smallOrderFee : 0;
        
        setCart({
          items: guestItems,
          subtotal,
          shipping,
          small_order_fee: smallOrderSurcharge,
          total: subtotal + shipping + smallOrderSurcharge,
          total_weight: totalWeight
        });
      } else {
        setCart({ items: [], subtotal: 0, shipping: 0, total: 0 });
      }
    }
  }, [user, storeSettings]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Guest cart functions
  const getGuestCart = () => {
    const saved = localStorage.getItem('guest_cart');
    return saved ? JSON.parse(saved) : [];
  };

  const saveGuestCart = (items) => {
    localStorage.setItem('guest_cart', JSON.stringify(items));
  };

  const addToCart = async (productId, quantity = 1, selectedSize = null) => {
    // If user is logged in, use server cart
    if (user) {
      try {
        await axios.post(`${API}/cart/add`, { 
          product_id: productId, 
          quantity,
          selected_size: selectedSize 
        }, { withCredentials: true });
        await fetchCart();
        setCartOpen(true);
        toast.success('Added to cart');
        return true;
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to add to cart');
        return false;
      }
    }
    
    // Guest cart - store in localStorage
    try {
      // Fetch product details
      const response = await axios.get(`${API}/products/${productId}`);
      const product = response.data;
      
      // Get the price for the selected size if applicable
      let itemPrice = product.price;
      let originalPrice = product.price;
      
      // Apply flash sale price if available
      if (product.flash_sale) {
        itemPrice = product.flash_sale.sale_price;
        originalPrice = product.flash_sale.original_price;
      }
      
      if (selectedSize && product.sizes) {
        const sizeObj = product.sizes.find(s => s.name === selectedSize);
        if (sizeObj) {
          itemPrice = product.flash_sale ? 
            Math.round(sizeObj.price * (1 - product.flash_sale.discount_percentage / 100) * 100) / 100 : 
            sizeObj.price;
          originalPrice = sizeObj.price;
        }
      }
      
      const itemWeight = product.weight || 0.5;
      // Auto-detect grams
      const normalizedWeight = itemWeight > 50 ? itemWeight / 1000 : itemWeight;
      
      const guestCart = getGuestCart();
      const existingIndex = guestCart.findIndex(
        item => item.product_id === productId && item.selected_size === selectedSize
      );
      
      if (existingIndex >= 0) {
        guestCart[existingIndex].quantity += quantity;
        guestCart[existingIndex].item_total = guestCart[existingIndex].unit_price * guestCart[existingIndex].quantity;
      } else {
        guestCart.push({
          product_id: productId,
          product: { name: product.name, image_url: product.image_url, price: product.price },
          name: product.name,
          price: itemPrice,
          unit_price: itemPrice,
          original_price: originalPrice,
          item_total: itemPrice * quantity,
          image_url: product.image_url,
          weight: normalizedWeight,
          quantity,
          selected_size: selectedSize,
          flash_sale: product.flash_sale || null
        });
      }
      
      saveGuestCart(guestCart);
      
      // Use dynamic store settings
      const freeThreshold = storeSettings?.free_shipping_threshold || 350;
      const minOrder = storeSettings?.minimum_order_value || 15;
      const smallFee = storeSettings?.small_order_surcharge || 0.50;
      
      // Calculate totals with weight-based shipping
      const subtotal = guestCart.reduce((sum, item) => {
        const price = parseFloat(item.unit_price) || parseFloat(item.price) || 0;
        return sum + (price * (parseInt(item.quantity, 10) || 1));
      }, 0);
      const totalWeight = guestCart.reduce((sum, item) => {
        let w = item.weight || 0.5;
        if (w > 50) w = w / 1000;
        return sum + (w * item.quantity);
      }, 0);
      let shipping = 0;
      if (subtotal < freeThreshold) {
        if (totalWeight <= 1) shipping = 4.00;
        else if (totalWeight <= 2) shipping = 5.48;
        else if (totalWeight <= 5) shipping = 7.49;
        else if (totalWeight <= 10) shipping = 7.49;
        else if (totalWeight <= 15) shipping = 11.99;
        else shipping = 11.99 + (Math.floor((totalWeight - 15) / 15) + 1) * 11.99;
      }
      
      const smallOrderSurcharge = subtotal < minOrder ? smallFee : 0;
      
      setCart({
        items: guestCart,
        subtotal,
        shipping,
        small_order_fee: smallOrderSurcharge,
        total: subtotal + shipping + smallOrderSurcharge,
        total_weight: totalWeight
      });
      
      setCartOpen(true);
      toast.success('Added to cart');
      return true;
    } catch (error) {
      toast.error('Failed to add to cart');
      return false;
    }
  };

  const updateCartItem = async (productId, quantity, selectedSize = null) => {
    // If user is logged in, use server cart
    if (user) {
      try {
        await axios.put(`${API}/cart/update`, { 
          product_id: productId, 
          quantity,
          selected_size: selectedSize 
        }, { withCredentials: true });
        await fetchCart();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update cart');
      }
      return;
    }
    
    // Guest cart update
    const guestCart = getGuestCart();
    
    // Use dynamic store settings
    const freeThreshold = storeSettings?.free_shipping_threshold || 350;
    const minOrder = storeSettings?.minimum_order_value || 15;
    const smallFee = storeSettings?.small_order_surcharge || 0.50;
    
    const calcGuestCartTotals = (items) => {
      const subtotal = items.reduce((sum, item) => {
        const price = parseFloat(item.unit_price) || parseFloat(item.price) || 0;
        return sum + (price * (parseInt(item.quantity, 10) || 1));
      }, 0);
      const totalWeight = items.reduce((sum, item) => {
        let w = item.weight || 0.5;
        if (w > 50) w = w / 1000;
        return sum + (w * item.quantity);
      }, 0);
      let shipping = 0;
      if (subtotal < freeThreshold) {
        if (totalWeight <= 1) shipping = 4.00;
        else if (totalWeight <= 2) shipping = 5.48;
        else if (totalWeight <= 5) shipping = 7.49;
        else if (totalWeight <= 10) shipping = 7.49;
        else if (totalWeight <= 15) shipping = 11.99;
        else shipping = 11.99 + (Math.floor((totalWeight - 15) / 15) + 1) * 11.99;
      }
      const small_order_fee = subtotal < minOrder ? smallFee : 0;
      return { subtotal, shipping, small_order_fee, total: subtotal + shipping + small_order_fee, total_weight: totalWeight };
    };
    
    if (quantity <= 0) {
      const filtered = guestCart.filter(
        item => !(item.product_id === productId && item.selected_size === selectedSize)
      );
      saveGuestCart(filtered);
      setCart({ items: filtered, ...calcGuestCartTotals(filtered) });
    } else {
      const index = guestCart.findIndex(
        item => item.product_id === productId && item.selected_size === selectedSize
      );
      if (index >= 0) {
        guestCart[index].quantity = quantity;
        guestCart[index].item_total = (guestCart[index].unit_price || guestCart[index].price) * quantity;
        saveGuestCart(guestCart);
        setCart({ items: guestCart, ...calcGuestCartTotals(guestCart) });
      }
    }
  };

  const clearCart = async () => {
    if (user) {
      try {
        await axios.delete(`${API}/cart/clear`, { withCredentials: true });
        await fetchCart();
      } catch {
        // ignore
      }
    } else {
      // Guest cart - clear localStorage
      localStorage.removeItem('guest_cart');
      setCart({ items: [], subtotal: 0, shipping: 0, total: 0 });
    }
  };

  return (
    <CartContext.Provider value={{ cart, cartOpen, setCartOpen, addToCart, updateCartItem, clearCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { checkAuth } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
          localStorage.setItem('token', response.data.token);
          await checkAuth();
          toast.success(`Welcome, ${response.data.name}!`);
          navigate('/', { replace: true });
        } catch {
          toast.error('Authentication failed');
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-[#1A2B3C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Track page visits (fire-and-forget)
  useEffect(() => {
    if (location.pathname.startsWith('/admin-portal')) return;
    axios.post(`${API}/track/visit`, { path: location.pathname }).catch(() => {});
  }, [location.pathname]);

  // Check for session_id in URL fragment BEFORE rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/shop/:category" element={<ShopPage />} />
      <Route path="/product/:productId" element={<ProductPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
      <Route path="/orders" element={<OrdersPage />} />
      <Route path="/wishlist" element={<WishlistPage />} />
      <Route path="/deals" element={<DealsPage />} />
      <Route path="/referral" element={<ReferralPage />} />
      <Route path="/return-policy" element={<ReturnPolicyPage />} />
      <Route path="/cookie-policy" element={<CookiePolicyPage />} />
      <Route path="/booking" element={<BookingPage />} />
      <Route path="/booking/confirmation" element={<BookingConfirmationPage />} />
      <Route path="/staff-dashboard" element={<StaffDashboard />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/admin-portal" element={<AdminPortal />} />
      <Route path="/admin-portal/*" element={<AdminPortal />} />
    </Routes>
  );
};

// Lazy load pages
const HomePage = React.lazy(() => import('./pages/HomePage'));
const ShopPage = React.lazy(() => import('./pages/ShopPage'));
const ProductPage = React.lazy(() => import('./pages/ProductPage'));
const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage'));
const CheckoutSuccessPage = React.lazy(() => import('./pages/CheckoutSuccessPage'));
const CheckoutCancelPage = React.lazy(() => import('./pages/CheckoutCancelPage'));
const OrdersPage = React.lazy(() => import('./pages/OrdersPage'));
const WishlistPage = React.lazy(() => import('./pages/WishlistPage'));
const DealsPage = React.lazy(() => import('./pages/DealsPage'));
const AdminPortal = React.lazy(() => import('./pages/AdminPortal'));
const CookiePolicyPage = React.lazy(() => import('./pages/CookiePolicyPage'));
const ReferralPage = React.lazy(() => import('./pages/ReferralPage'));
const ReturnPolicyPage = React.lazy(() => import('./pages/ReturnPolicyPage'));
const BookingPage = React.lazy(() => import('./pages/BookingPage'));
const BookingConfirmationPage = React.lazy(() => import('./pages/BookingConfirmationPage'));
const StaffDashboard = React.lazy(() => import('./pages/StaffDashboard'));

// Cookie Consent Component
import CookieConsent from './components/CookieConsent';
import WhatsAppWidget from './components/WhatsAppWidget';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PushNotificationManager from './components/PushNotificationManager';
import SWUpdateNotifier from './components/SWUpdateNotifier';
import WelcomePopup from './components/WelcomePopup';

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="text-center">
      <div className="w-12 h-12 border-2 border-[#1A2B3C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-slate-600 font-medium tracking-wide">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <StoreSettingsProvider>
          <AuthProvider>
            <CartProvider>
              <Toaster position="top-right" richColors />
              <React.Suspense fallback={<PageLoader />}>
                <AppRouter />
              </React.Suspense>
              <CookieConsent />
              <WhatsAppWidget />
              <PWAInstallPrompt />
              <PushNotificationManager />
              <SWUpdateNotifier />
              <WelcomePopup />
            </CartProvider>
          </AuthProvider>
        </StoreSettingsProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
